import { useState } from 'react';
import moment from 'moment';
import { LocaleUtils } from 'react-day-picker';
import useQueryString from 'next/hooks/use-query-string';

// Suppress moment deprecation warnings
moment.suppressDeprecationWarnings = true;

// Query string date format e.g. 01-07-2021
export const QUERY_FORMAT = 'MM-DD-YYYY';

// Readable date format e.g. Jan 7, 2021
export const DATE_FORMAT = 'MMM D, YYYY';

/**
 * Full date range options
 */
export const RANGE_OPTIONS = [
  {
    label: 'Last 7 days',
    value: 7,
  },
  {
    label: 'Last 30 days',
    value: 30,
  },
  {
    label: 'Last 60 days',
    value: 60,
  },
  {
    label: 'Last 90 days',
    value: 90,
  },
  {
    label: 'Current quarter',
    value: 'current-quarter',
  },
  {
    label: 'Previous quarter',
    value: 'previous-quarter',
  },
  {
    label: 'Custom',
    value: 'custom',
  },
];

/**
 * Earliest year for options
 *
 * Currently set to 2013 - the year Appcues was founded
 */
const EARLIEST_YEAR = 2013;

/**
 * Year options
 */
export const YEAR_OPTIONS = Array.from({
  length: new Date().getFullYear() - EARLIEST_YEAR + 1,
}).map((_, index) => ({
  label: `${EARLIEST_YEAR + index}`,
  value: EARLIEST_YEAR + index,
}));

/**
 * Month options
 */
export const MONTH_OPTIONS = LocaleUtils.getMonths().map((month, index) => ({
  label: month,
  value: index,
}));

/**
 * Get end of today
 *
 * @return {Date} End of today
 */
export const getEndOfToday = () => {
  return moment().endOf('day').toDate();
};

/**
 * Determine recommended frequency for time scale reports. By default, it will
 * use a monthly frequency for ranges greater than 90 days and daily for shorter
 * ranges.
 *
 * @param {DateRange} dates - Start/end dates
 * @param {number} threshold - Number of days to allow for daily
 * @return {('day'|'month')} Recommended frequency
 */
export const getRecommendedFrequency = ({ start, end }, threshold = 90) => {
  const duration = moment(end).diff(moment(start), 'days', true);
  return Math.ceil(duration) > threshold ? 'month' : 'day';
};

/**
 * Format the date value for display in the input
 *
 * @param {Date} date - Date value of input
 * @param {string} format - Date format
 * @return {string} Formatted date for display
 */
export const formatDate = (date, format = DATE_FORMAT) => {
  return moment(date).format(format);
};

/**
 * Parse the input value into a date
 *
 * @param {value} value - Input value
 * @return {Date|null} Parsed date or null
 */
export const parseDate = value => {
  const date = moment(value);
  return date.isValid() ? date.toDate() : null;
};

/**
 * Validate input value is parseable
 *
 * @param {string} value - Input value
 * @param {boolean} future - Whether future dates are allowed
 * @return {boolean} Whether value is a parseable
 */
export const validateDate = (value, future) => {
  if (value === '') {
    return true;
  }

  const date = moment(value);
  const valid = date.isValid();

  if (valid && future) {
    return true;
  }

  const endOfToday = getEndOfToday();
  return valid && date.isSameOrBefore(endOfToday);
};

/**
 * Normalize start/end dates by pushing to lower/upper ends
 *
 * @param {DateRange} dates - Start and end dates
 * @return {object<Date>} Normalized dates
 */
export const normalizeDates = ({ start, end }) => ({
  start: start && moment(start).startOf('day').toDate(),
  end: end && moment(end).endOf('day').toDate(),
});

/**
 * Sanitize dates by pushing start and end to their respective bounds and
 * checking if start is after end and vice versa
 *
 * @param {DateRange} dates - Start and end dates
 * @param {Date} dates.start - Original start date
 * @param {Date} dates.end - Original end date
 * @param {('start'|'end')} changed - Date that was changed
 * @return {DateRange} Sanitized dates
 */
export const sanitizeDates = (dates = {}, changed = 'start') => {
  const { start, end } = dates;

  const other = changed === 'start' ? 'end' : 'start';

  // Based on which date was changed, check whether the date is before/after
  // the other date e.g. start after end or end before start
  const invalid =
    changed === 'start' ? dates[changed] > end : dates[changed] < start;

  // Reset other date if range invalid
  const sanitized = {
    [changed]: dates[changed],
    [other]: invalid ? undefined : dates[other],
  };

  // Normalize sanitized dates
  return normalizeDates(sanitized);
};

/**
 * Calculate start/end dates for range value
 *
 * @param {number|string}} range - Date range type
 * @param {function} override - Range calculator override
 * @return {DateRange} Start/end dates for range
 */
export const calculateDatesFor = (range, override) => {
  // Use calculate override if it exists and returns a value
  const overridden = override?.(range);
  if (overridden) {
    return overridden;
  }

  // Attempt to coerce range into a number
  const parsed = Number.parseInt(range, 10);

  // Use a virtual tail end date for calculations to prevent off-by-one errors
  // when subtracting days/quarters from today
  const tail = moment().add(1, 'day').startOf('day');

  switch (true) {
    case typeof parsed === 'number' && !Number.isNaN(parsed): {
      return {
        range,
        start: tail.subtract(parsed, 'days').startOf('day').toDate(),
        end: getEndOfToday(),
      };
    }

    case range === 'current-quarter': {
      return {
        range,
        start: tail.startOf('quarter').toDate(),
        end: getEndOfToday(),
      };
    }

    case range === 'previous-quarter': {
      const previous = tail.subtract(1, 'quarter');
      return {
        range,
        start: previous.clone().startOf('quarter').toDate(),
        end: previous.clone().endOf('quarter').toDate(),
      };
    }

    case range === 'custom':
    default:
      return { range, start: null, end: null };
  }
};

/**
 * Hook for managing date ranges
 *
 * @param {Option[]} options - Valid range options
 * @param {DateRange} initial - Iniital date range values
 * @param {function} calculate - Range calculator override
 * @return {[DateRange, function]} Calculated date range values and state setter
 */
export const useDateRange = (options = [], initial = {}, calculate) => {
  const { range } = initial;

  const normalized = !options.some(({ value }) => value === range)
    ? options[0]?.value
    : range;

  const dates =
    range && normalized !== 'custom'
      ? calculateDatesFor(normalized, calculate)
      : initial;

  const [value, setDateRange] = useState(dates);

  return [value, setDateRange];
};

/**
 * Hook for managing date ranges with query string support
 *
 * @param {Option[]} options - Valid range options
 * @param {DateRange} initial - Iniital date range values
 * @param {function} calculate - Range calculator override
 * @return {[DateRange, function]} Calculated date range values and state setter
 */
export const useDateRangeQuery = (options = [], initial = {}, calculate) => {
  // Range query string hook
  const [range, setRange] = useQueryString('range', initial.range, {
    validate: value => options.find(option => option.value === value),
    parse: value => Number.parseInt(value, 10) || value,
  });

  // Whether the current range type is custom
  const custom = range === 'custom';

  // Shared query string hooks for start and end
  const hooks = {
    validate: value => Boolean(parseDate(value)),
    serialize: value => (custom ? formatDate(value, QUERY_FORMAT) : null),
    parse: value => parseDate(value),
  };

  // Start and end query string hooks
  const [start, setStart] = useQueryString('start', initial.start, hooks);
  const [end, setEnd] = useQueryString('end', initial.end, hooks);

  const [dates, setDateRange] = useDateRange(
    options,
    { range, start, end },
    calculate
  );

  // Normalize and calculate date range based on range type
  // const dates = !custom ? calculateDatesFor(range) : { range, start, end };

  // State updater
  const setState = next => {
    setDateRange(next);
    setRange(next.range);
    setStart(next.start);
    setEnd(next.end);
  };

  return [dates, setState];
};
